<template>
    <div>
        <MainModal 
            :show-modal="showModal"
            size="md" 
            :title="title" 
            @close="closeModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="client__modal__body scroll-height">
                        <FormInput 
                            v-if="hasField('name')"
                            id="name" v-model="form.name"
                            size="sm" 
                            info-type="required" 
                            label="Client or Brand Name"
                        />

                        <div v-if="hasField('industry') || hasField('status')" class="form__row mb--15 pl--3">
                            <div v-if="hasField('industry')" class="flex-1 mr--5">
                                <FormLabel title="Industry" info-type="required" />
                                <CustomDropdown 
                                    id="industryDropdown" 
                                    v-model="form.industry" 
                                    :options="industries"
                                    size="md"
                                    btn-classes="text--capitalize text--normal" 
                                    list-item-class="text--capitalize" 
                                    list-type="industries" 
                                    @search="searchIndustries" 
                                    @select="v => form.industry = v" 
                                />
                            </div>

                            <div v-if="hasField('status')" class="flex-1 mr--5">
                                <FormLabel title="Status" />
                                <CustomDropdown 
                                    id="statusDropdown" 
                                    v-model="form.status" 
                                    :options="CLIENT_STATUSES"
                                    :searchable="false"
                                    btn-classes="text--capitalize text--normal" 
                                    list-item-class="text--capitalize" 
                                    list-type="industries" 
                                    @select="v => form.status = v" 
                                >
                                    <template #text="{ item }">
                                        <pre>{{ item }}
                                            </pre>
                                        <div class="w-100">
                                            <Status type="client" :value="item" />
                                        </div>
                                    </template>
                                </CustomDropdown>
                            </div>
                        </div>

                        <div v-if="hasField('email')">
                            <FormLabel title="Email & Contact Info" label-type="menu" class="contact--label" />
                            
                            <FormInput 
                                v-if="hasField('email')"
                                id="title" 
                                v-model="form.email"
                                type="email" 
                                size="sm" 
                                class="flex-1 mr--5"
                                label="Email address" 
                                :is-error="fieldsError.email && fieldsError.email.show" 
                                :err-msg="fieldsError.email && fieldsError.email.msg" 
                                @change="checkEmail"
                            />

                            <div class="form__row mb--15 pl--3">
                                <div v-if="hasField('country')" class="flex-1 mr--5">
                                    <FormLabel title="Country" />
                                    <CustomDropdown 
                                        id="countryDropdown" 
                                        v-model="form.country" 
                                        :options="countries"
                                        size="md"
                                        list-type="countries" 
                                        index-label="name"
                                        @search="searchCountries" 
                                        @select="selectCountry" 
                                    />
                                </div>
                                
                                <div v-if="hasField('phoneNumber')" class="flex-1 mr--5">
                                    <FormInput 
                                        id="phoneNumber"
                                        v-model="form.phoneNumber"
                                        type="text" 
                                        :add-on-text="form.country.dialCode"
                                        size="sm" 
                                        no-margin-bottom
                                        label="Phone Number"                                    
                                    />
                                </div>
                            </div>
                            
                            <div class="form__row mb--15 pl--3">
                                <div v-if="hasField('address')" class="flex-1 mr--5">
                                    <FormInput 
                                        id="address"
                                        v-model="form.address" 
                                        label="Address"  
                                        no-margin-bottom
                                        type="text" size="sm"
                                    />
                                </div>

                                <div v-if="hasField('state')" class="flex-1 mr--5">
                                    <FormInput 
                                        id="city" 
                                        v-model="form.state"           
                                        label="State" 
                                        no-margin-bottom
                                        type="text" 
                                        size="sm"
                                    />
                                </div>
                            </div>

                            <div class="form__row mb--15 pl--3">
                                <div v-if="hasField('city')" class="flex-1 mr--5">
                                    <FormInput 
                                        id="city" 
                                        v-model="form.city"           
                                        label="City" 
                                        no-margin-bottom
                                        type="text" 
                                        size="sm"
                                    />
                                </div>
                                <div v-if="hasField('city')" class="flex-1 mr--5">
                                    <FormInput 
                                        id="zipCode"
                                        v-model="form.zipCode" 
                                        label="Zip code"  
                                        no-margin-bottom
                                        type="text" size="sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div v-if="hasField('ccEmails') && actionType === 'edit'" class="form__item mb--0">
                            <Alert type="secondary" size="sm" class="mb--15">
                                <template #text>
                                    <p class="text--sm">
                                        Useful for clients with more than a single correspondent, e.g john@ziner.com, jane@ziner.com.
                                    </p>
                                </template>
                            </Alert>  

                            <FormLabel title="Corresponding emails" />
                            <MultipleBillingEmails
                                :is-flex="false" :items="form.ccEmails"
                                classes="w-100" :max-limit="6"
                                @add="addEmail" @delete="removeEmail"
                            />
                        </div>
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        :disabled="loading"
                        @submit="closeModal"
                    />
                    <BaseButton 
                        :label="btnLabel"
                        :loading="loading" 
                        :disabled="isBtnDisabled"
                        @submit="onComplete"                                       
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import countryList from "@/assets/js/acceptable_countries.json";
import MainModal from "./MainModal.vue";
import FormLabel from "../forms/FormLabel.vue";
import Status from "../status/index.vue";
import { industryData } from "@/utils/dummy";
import MultipleBillingEmails from "../input/MultipleBillingEmails.vue";
import { validateEmail } from "@/utils/helpers";
import BaseButton from "../buttons/BaseButton.vue";
import FormInput from "../input/FormInput.vue";
import { CLIENT_STATUSES } from "@/utils/enums";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import { updateClientDetailsFields } from "@/components/dataComponents/auth";
import Alert from "../alert/Alert.vue";

export default {
    name: "CreateClient",

    components: {
        MainModal,
        FormLabel,
        MultipleBillingEmails,
        BaseButton,
        FormInput,
        CustomDropdown,
        Alert,
        Status,
    },

    props: {
        actionType: { type: String, default: "add" },
        currentSelection: { type: String, default: "create" },
        showBillingInput: { type: Boolean, default: true },
        currentClient: { type: Object, default: () => {} },
        showModal: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
    },

    data() {
        return {
            countries: countryList,
            form: {
                _id: "",
                name: "",
                email: "",
                phoneNumber: "",
                country: {},
                address: "",
                industry: "",
                ccEmails: [],
                dialCode: "",
                status: "",
                rating: 0,
                zipCode: "",
                state: "",
                city: "",
            },
            industries: industryData, 
            isEmailInvalid: false,
            CLIENT_STATUSES,
            fields: updateClientDetailsFields,
            fieldsError: {},
        };
    },

    computed: {
        isBtnDisabled() {
            if (!this.form.name) {
                return true;
            } 
            
            if (this.loading) {
                return true;
            } 
            
            if (Object.keys(this.fieldsError).length > 0) {
                return true;
            }

            return false;
        },

        numberOfBillingEmailsLeft() {
            if (this.form.ccEmails.length > 0) {
                return 3 - this.form.ccEmails.length;
            } else {
                return 0;
            }
        },

        clientCount() {
            return this.$store.getters["clients/pageData"]?.total;
        },

        btnLabel() {
            if (this.actionType === "edit") {
                return "Save updates";
            } else {
                return "Add client";
            }
        },

        title() {
            return this.fields[this.currentSelection]?.title || "";
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },
    },

    watch: {
        currentClient: {
            handler(newVal, oldVal) {
                if (newVal?._id || oldVal?._id) {
                    this.fillForm(newVal);
                }
            },
            immediate: true,
            deep: true,
        },

        currentSelection(newVal) {
            if (newVal) {
                if (this.fields[newVal] && this.fields[newVal]?.fields) {
                    this.populateFormBasedOnSelection(newVal);
                }
            }
        },
    },

    methods: {
        checkEmail(event) {     
            if (this.fieldsError.email) {
                this.fieldsError.email.show = false;
                this.fieldsError.email.msg = "";
            }    

            const val = event?.target?.value;

            // if (!val) {
            //     this.fieldsError.email.show = true;
            //     this.fieldsError.email.msg = "Please enter a valid email address";
            //     return;
            // }

            if (!val.includes("@")) {
                this.fieldsError.email.show = true;
                this.fieldsError.email.msg = "Incorrect email address";
                return;
            }
            
            // prevents users from adding their email address as a client email
            if (val === this.profile?.email) {
                this.fieldsError.email.show = true;
                this.fieldsError.email.msg = "The email address associated with this account is not allowed as a client address";
                return;
            }
        },

        hasField(field) {
            return !this.fields[this.currentSelection] ? false : this.fields[this.currentSelection].fields[field]; 
        },

        populateFormBasedOnSelection(val) {
            const selectedItem = this.fields[val]?.fields;

            for (const key in selectedItem) {
                if (this.currentClient.hasOwnProperty(key)) {
                    this.form[key] = this.currentClient[key];
                }
            }
        },

        closeModal() {
            this.$emit("cancel", false);
        },

        resetForm() {
            this.form = {
                name: "",
                email: "",
                phoneNumber: "",
                country: "",
                address: "",
                industry: "",
                ccEmails: [],
                dialCode: "",
                status: "",
                zipCode: "",
                state: "",
                city: "",
            };
        },

        fillForm(val) {
            this.form = {
                _id: val?._id || "",
                name: val?.name || "",
                email: val?.email || "",
                phoneNumber: val?.phoneNumber || "",
                country: val?.country || "",
                zipCode: val?.zipCode || "",
                city: val?.city || "",
                state: val?.state || "",
                address: val?.address || "",
                industry: val?.industry || "",
                ccEmails: val?.ccEmails || [],
                status: val?.status || "",
            };
        },

        validateEmailInput(e) {
            const value = e?.target?.value;

            if (!value.includes("@")) return;

            const res = validateEmail(value);
             
            if (!res) {
                this.isEmailInvalid = true;
            } else {
                this.isEmailInvalid = false;
            }
        },

        buildPayload() {
            const payload = {
                name: this.form.name,
                email: this.form.email || "",
                phoneNumber: this.form.phoneNumber || "",
                ccEmails: this.form.ccEmails.join(","),
                country: this.form.country?.name || this.form.country || "",
                address: this.form.address || "",
                industry: this.form.industry || "",
                city: this.form.city,
                state: this.form.state,
                address: this.form.address,
                zipCode: this.form.zipCode,
                status: this.form.status || "inactive",
            };

            if (this.form._id) {
                payload._id = this.form._id;
            }

            return payload;
        },

        onComplete() {
            const payload = this.buildPayload();
            this.$emit("complete", { payload, resetFn: this.resetForm  });
        },

        removeEmail(email) {
            this.form.ccEmails = this.form.ccEmails.filter(item => item !== email);
        },

        addEmail(value) {
            if (!value || this.form.ccEmails.includes(value)) return;

            this.form.ccEmails.push(value);
        },

        computePhoneNumber(dialCode) {
            if (dialCode) {
                this.form.dialCode = dialCode;
            }
            return;
        },

        upgradeAccount() {
            this.closeModal();
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        searchCountries(val) {
            this.countries = countryList.filter(item => item.name.toLowerCase().includes(val));
        },

        selectCountry(item) {
            this.form.country = item;
        },

        searchIndustries(val) {                        
            this.industries = industryData.filter(item => item.toLowerCase().includes(val));
        },
    },
};
</script>

<style lang="scss" scoped>
.client__modal__body {    
    &.scroll-height {
        // max-height: 490px;
        // overflow: scroll;

        .form--control {
            &:not(:last-of-type) {
                margin-bottom: 15px !important;
            }
        }
    }
}

.contact--label {
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
    margin-bottom: 15px !important;
    margin-top: 20px !important;
}
</style>